import { useState } from "react";
import { Link } from "react-router-dom";
import { submitStartup } from "../lib/api.js";
import TurnstileWidget from "../components/TurnstileWidget.js";

function isValidUrl(str: string): boolean {
  return /^https?:\/\/.+\..+/.test(str.trim());
}

export default function Submit() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");

  function validateUrl(value: string) {
    setUrl(value);
    if (value.trim() && !isValidUrl(value)) {
      setUrlError("URL must start with http:// or https://");
    } else {
      setUrlError(null);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim()) return;

    if (!isValidUrl(url)) {
      setUrlError("URL must start with http:// or https://");
      return;
    }

    if (!turnstileToken) {
      setError("Complete verification before submitting");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await submitStartup({
        url: url.trim(),
        turnstile_token: turnstileToken,
      });
      setResult(res);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    if (result.status === "duplicate") {
      return (
        <div className="max-w-lg mx-auto text-center animate-fade-in">
          <div className="card-glow py-10">
            <div className="text-4xl mb-3">{"👋"}</div>
            <h2 className="text-xl font-bold mb-2 text-white">
              Already on StartupVibes!
            </h2>
            <p className="text-slate-400 mb-6">
              This startup is already being tracked. Go vote on it!
            </p>
            <Link
              to={`/startup/${result.existing_startup?.slug}`}
              className="btn-primary inline-block"
            >
              View {result.existing_startup?.name} {"\u2192"}
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-lg mx-auto text-center animate-fade-in">
        <div className="card-glow py-10">
          <div className="text-5xl mb-3 animate-bounce-in">{"🎉"}</div>
          <h2 className="text-xl font-bold gradient-text mb-2">
            Submitted!
          </h2>
          <p className="text-slate-400 mb-6 max-w-sm mx-auto">
            {result.status === "already_submitted"
              ? "This startup was already submitted and is pending review."
              : "Your submission is pending moderation. It'll appear on the site once approved."}
          </p>
          {result.auto_metadata && (
            <div className="bg-slate-800/60 rounded-2xl p-5 text-left mb-6 border border-slate-700/30 mx-4">
              <p className="font-semibold text-white">
                {result.auto_metadata.title || "Unknown"}
              </p>
              <p className="text-sm text-slate-400 mt-1">
                {result.auto_metadata.description || "No description found"}
              </p>
              <p className="text-xs text-slate-500 mt-2">
                {result.auto_metadata.domain}
              </p>
            </div>
          )}
          <Link to="/" className="btn-secondary inline-block">
            {"\u2190"} Back to feed
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto animate-fade-in">
      <div className="text-center mb-8">
        <div className="text-4xl mb-3">{"🚀"}</div>
        <h1 className="text-3xl font-extrabold mb-2">
          <span className="gradient-text">Submit a Startup</span>
        </h1>
        <p className="text-slate-400">
          Paste the company URL and we'll pull metadata automatically.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card-glow space-y-6">
        <div>
          <label className="block text-sm text-slate-400 mb-2 font-medium">
            Company URL
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <svg
                className="w-4 h-4 text-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
            </div>
            <input
              type="text"
              value={url}
              onChange={(e) => validateUrl(e.target.value)}
              placeholder="https://example.com"
              className={`input pl-10 ${
                urlError
                  ? "border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20"
                  : ""
              }`}
            />
          </div>
          {urlError && (
            <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {urlError}
            </p>
          )}
        </div>

        <TurnstileWidget onToken={setTurnstileToken} />

        {error && (
          <p className="text-red-400 text-sm flex items-center gap-1.5">
            <span>{"⚠️"}</span> {error}
          </p>
        )}

        {!turnstileToken && url.trim() && (
          <p className="text-amber-400/80 text-xs flex items-center gap-1">
            Complete verification to submit
          </p>
        )}

        <button
          type="submit"
          disabled={loading || !url.trim() || !!urlError || !turnstileToken}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Submitting...
            </span>
          ) : (
            "Submit Startup \u{1F680}"
          )}
        </button>
      </form>
    </div>
  );
}
